import React, { useEffect, useState } from "react";
import { message, Select, Spin } from "antd";
import { useSelector } from "react-redux";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { fetchCustomers } from "../customer/FetchCustomers";
import { fetchFamilyMember } from "../familymember/fetchFamilyMember";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { token } from "../../constants/Constants";
import { get_customers_with_multiple_orders, get_monthly_orders_count, get_total_b2c_price, get_total_discount } from "../../endpoints/API";

const { Option } = Select;

const MIS = () => {
  // State declarations
  const [customers, setCustomers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [repeatedCustomersCount, setRepeatedCustomersCount] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [monthlyOrders, setMonthlyOrders] = useState([]);

  const [month, setMonth] = useState(0);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [monthOptions, setMonthOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Environment variables
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const urlPrefix = prodTestUrl || baseUrl;

  // Setup dropdown options
  const generateMonthYearOptions = () => {
    const months = Array.from({ length: 13 }, (_, i) => i.toString());
    const formattedMonths = months.map((m) => ({
      value: m,
      label:
        m === "0"
          ? "All Months"
          : new Date(2000, parseInt(m) - 1).toLocaleString("default", {
            month: "long",
          }),
    }));
    setMonthOptions(formattedMonths);

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: currentYear - 2021 + 1 }, (_, i) =>
      (2022 + i).toString()
    );
    setYearOptions(years);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await fetchCustomers(setCustomers, baseUrl, prodTestUrl);
      await fetchFamilyMember(setPatients, baseUrl, prodTestUrl);

      const [repeatedRes, revenueRes, discountRes, monthlyOrdersRes] = await Promise.all([
        get_customers_with_multiple_orders(),
        get_total_b2c_price(),
        get_total_discount(),
        get_monthly_orders_count(month, year),
      ]);

      const repeatedData = repeatedRes.data;
      const revenueData = revenueRes.data;
      const discountData = discountRes.data;
      const monthlyData = monthlyOrdersRes.data;

      setRepeatedCustomersCount(repeatedData.customers_with_multiple_orders);
      setTotalRevenue(revenueData.total_b2c_price);
      setTotalDiscount(discountData.total_discount_given);

      const formattedData = monthlyData.map(item => ({
        month: `${item.month.slice(0, 3)} ${year}`,
        count: item.count,
      }));

      setMonthlyOrders(formattedData);
    } catch (error) {
      message.error(error?.response?.data?.error || "Failed to fetch data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    generateMonthYearOptions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [month, year, baseUrl, prodTestUrl]);

  // Render
  return (
    <div style={{ padding: "8px" }}>
      <style>{`
        .mis-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 24px;
        }
        .mis-stat-card {
          background: #f5f5f5;
          border-radius: 8px;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .mis-stat-label {
          font-size: 13px;
          color: #888;
        }
        .mis-stat-value {
          font-size: 20px;
          font-weight: 700;
          color: #1890ff;
        }
        .mis-chart-filters {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }
        .mis-chart-filters .ant-select {
          flex: 1 1 140px;
          min-width: 0;
        }

        @media (max-width: 768px) {
          .mis-stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .mis-stat-value {
            font-size: 17px;
          }
        }

        @media (max-width: 480px) {
          .mis-stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          .mis-stat-card {
            padding: 10px 12px;
          }
          .mis-stat-label {
            font-size: 11px;
          }
          .mis-stat-value {
            font-size: 15px;
          }
          .mis-chart-filters .ant-select {
            flex: 1 1 100%;
          }
        }
      `}</style>

      {isLoading ? (
        <Spin size="large" style={{ display: "block", marginTop: 50 }} />
      ) : (
        <>
          {/* Stats Cards */}
          <div className="mis-stats-grid">
            <div className="mis-stat-card">
              <span className="mis-stat-label">Total Unique Customers</span>
              <span className="mis-stat-value">{customers.length}</span>
            </div>
            <div className="mis-stat-card">
              <span className="mis-stat-label">Total Patients</span>
              <span className="mis-stat-value">{patients.length}</span>
            </div>
            <div className="mis-stat-card">
              <span className="mis-stat-label">Repeated Customers</span>
              <span className="mis-stat-value">{repeatedCustomersCount}</span>
            </div>
            <div className="mis-stat-card">
              <span className="mis-stat-label">Total Revenue</span>
              <span className="mis-stat-value">₹{totalRevenue.toLocaleString()}</span>
            </div>
            <div className="mis-stat-card">
              <span className="mis-stat-label">Total Discount</span>
              <span className="mis-stat-value">₹{totalDiscount.toLocaleString()}</span>
            </div>
          </div>

          {/* Chart Section */}
          <div>
            <h3 style={{ marginBottom: 10, fontSize: 16, fontWeight: 600 }}>Monthly Orders</h3>

            <div className="mis-chart-filters">
              <Select
                value={month}
                onChange={setMonth}
                placeholder="Select Month"
                style={{ width: 180 }}
              >
                {monthOptions.map(({ value, label }) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
              <Select
                value={year}
                onChange={setYear}
                placeholder="Select Year"
                style={{ width: 120 }}
              >
                {yearOptions.map((y) => (
                  <Option key={y} value={y}>
                    {y}
                  </Option>
                ))}
              </Select>
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={monthlyOrders}
                margin={{ top: 5, right: 16, left: 0, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12 }}
                  label={{
                    value: "Month & Year",
                    position: "insideBottom",
                    offset: -20,
                    style: { textAnchor: "middle", fontSize: 12 },
                  }}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  width={45}
                  label={{
                    value: "Number of Orders",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle", fontSize: 12 },
                  }}
                />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#1890ff"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default MIS;